import { api } from './client';
import type { Invitation } from './invitations.api';
import type { Trip, TripDestination, TripParticipant, Flight, Hotel, TripExportData, SharedTrip, TripShareSection } from '../types';

/** What both share endpoints answer with. */
export interface ShareState {
  shareToken: string | null;
  shareSections: TripShareSection[];
}

/** The shape `POST /trips/:id/dates/preview` answers with. */
export interface DatesPreview {
  plan: {
    dayCount: number;
    removedDays: number;
    movedStops: number;
    landsOnDayNumber: number | null;
    addedDays: number;
    /**
     * Meals planned on the days that disappear. ADR-0008: unlike the stops,
     * they are NOT rescued — they go with the day. This number is the whole
     * reason that is a decision and not silent data loss, so it must reach the
     * screen.
     */
    removedMeals: number;
    /** ADR-0017: days with clothes planned that go with the day. Same reason. */
    removedOutfitDays: number;
  };
  /** Reservations whose own dates fall outside the new range. Never moved. */
  outOfRange: {
    flights:  { id: string; flightNumber: string | null; departureTime: string }[];
    hotels:   { id: string; name: string; checkIn: string }[];
    bookings: { id: string; title: string; date: string }[];
  };
}

/** Someone else coming, entered while creating the trip (ADR-0021, ADR-0029). */
export interface NewTraveller {
  name: string;
  birthDate?: string;
  email?: string;
  /** ADR-0037: in an agency's trip, the client of its book. */
  clientId?: string;
}

export interface CreateTripInput {
  name: string;
  destination: string;
  destinationLat?: number;
  destinationLng?: number;
  startDate: string;
  endDate: string;
  nights: number;
  notes?: string;
  budgetCurrency?: string;
  contentLanguage?: string;
  travellers?: NewTraveller[];
  ownerBirthDate?: string;
  /** ADR-0030: the legs in order; their nights add up to the trip's. */
  destinations?: DestinationInput[];
  /** ADR-0033: created by staff for their agency; the first traveller owns it. */
  agencyId?: string;
  /** ADR-0033: an agency's circuit, cloned per departure. No travellers. */
  isTemplate?: boolean;
}

export interface DestinationInput {
  id?: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  nights: number;
}

export const tripsApi = {
  list: () => api.get<Trip[]>('/trips').then(r => r.data),
  get: (id: string) => api.get<Trip>(`/trips/${id}`).then(r => r.data),
  getCenter: (id: string) =>
    api.get<{ lat: number | null; lng: number | null }>(`/trips/${id}/center`).then(r => r.data),
  create: (data: CreateTripInput) => api.post<Trip & { invitesFailed: number }>('/trips', data).then(r => r.data),
  parseDates: (text: string) =>
    api.post<{ startDate: string; endDate: string; nights: number }>('/trips/parse-dates', { text }).then(r => r.data),
  suggestInterests: (destination: string, locale?: string) =>
    api.post<{ categories: { label: string; emoji?: string }[] }>('/trips/suggest-interests', { destination, locale }).then(r => r.data),
  update: (id: string, data: Partial<Trip>) => api.patch<Trip>(`/trips/${id}`, data).then(r => r.data),

  uploadCover: (id: string, image: Blob, filename: string) => {
    const form = new FormData();
    form.append('file', image, filename);
    return api.post<{ coverImageUrl: string }>(`/trips/${id}/cover`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  removeCover: (id: string) => api.delete<{ coverImageUrl: null }>(`/trips/${id}/cover`).then(r => r.data),
  delete: (id: string) => api.delete(`/trips/${id}`),
  replaceDestinations: (id: string, destinations: DestinationInput[]) =>
    api.patch<TripDestination[]>(`/trips/${id}/destinations`, { destinations }).then(r => r.data),
  // What a new range would do, before committing to it — the confirmation needs
  // a number, not a vague warning. Read-only on the server.
  previewDates: (id: string, startDate: string, endDate: string) =>
    api.post<DatesPreview>(`/trips/${id}/dates/preview`, { startDate, endDate }).then(r => r.data),
  toggleShare: (id: string) => api.post<ShareState>(`/trips/${id}/share`).then(r => r.data),

  // ADR-0012: which sections the public link may show. The server validates the
  // list against the Prisma enum — this is a preference, not a permission, and
  // it only ever narrows what an anonymous visitor can reach.
  updateShareSections: (id: string, sections: TripShareSection[]) =>
    api.patch<ShareState>(`/trips/${id}/share-sections`, { sections }).then(r => r.data),
  togglePublish: (id: string) => api.post<{ isPublished: boolean; publishedAt: string | null }>(`/trips/${id}/publish`).then(r => r.data),
  getShared: (token: string) => api.get<SharedTrip>(`/trips/share/${token}`).then(r => r.data),

  /**
   * ADR-0001: the shared map's load, measured. Nobody is logged in here, so the
   * server bills it to the trip's owner — the person who handed the link out.
   * Fire-and-forget, like `placesApi.reportMapLoad`.
   */
  reportSharedMapLoad: (token: string) =>
    api.post(`/trips/share/${token}/map-load`).catch(() => {}),

  /**
   * A story-sized background for the Instagram sticker. Goes through the API
   * rather than straight to R2 so the blob is same-origin and the export canvas
   * stays untainted — see server shareBackground.controller.ts.
   */
  shareBackground: (tripId: string, photoId: string) =>
    api.get<Blob>(`/trips/${tripId}/share-background`, {
      params: { photoId },
      responseType: 'blob',
    }).then(r => r.data),

  /** Transcode a picked file server-side. The fallback for HEIC, which no
   *  browser but Safari can decode. Nothing is stored. */
  convertShareBackground: (tripId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<Blob>(`/trips/${tripId}/share-background`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    }).then(r => r.data);
  },

  listParticipants: (tripId: string) =>
    api.get<TripParticipant[]>(`/trips/${tripId}/participants`).then(r => r.data),
  invite: (tripId: string, email: string, role: 'VIEWER' | 'EDITOR') =>
    api.post<TripParticipant>(`/trips/${tripId}/participants`, { email, role }).then(r => r.data),
  updateParticipant: (tripId: string, userId: string, role: 'VIEWER' | 'EDITOR') =>
    api.patch(`/trips/${tripId}/participants/${userId}`, { role }).then(r => r.data),
  removeParticipant: (tripId: string, userId: string) =>
    api.delete(`/trips/${tripId}/participants/${userId}`),

  // ADR-0021 — travellers without an account.
  createPlaceholder: (tripId: string, name: string) =>
    api.post<TripParticipant>(`/trips/${tripId}/placeholders`, { name }).then(r => r.data),
  /** ADR-0029: only a placeholder's date is the trip's to write; null clears it. */
  setPlaceholderBirthDate: (tripId: string, userId: string, birthDate: string | null) =>
    api.patch(`/trips/${tripId}/placeholders/${userId}`, { birthDate }),
  invitePlaceholder: (tripId: string, userId: string, email: string, role: 'VIEWER' | 'EDITOR', locale?: string) =>
    api.post<Invitation>(`/trips/${tripId}/placeholders/${userId}/invite`, { email, role, locale }).then(r => r.data),
  mergePlaceholder: (tripId: string, userId: string, intoUserId: string) =>
    api.post(`/trips/${tripId}/placeholders/${userId}/merge`, { intoUserId }),

  listFlights: (tripId: string) => api.get<Flight[]>(`/trips/${tripId}/flights`).then(r => r.data),
  createFlight: (tripId: string, data: Partial<Flight>) =>
    api.post<Flight>(`/trips/${tripId}/flights`, data).then(r => r.data),
  updateFlight: (tripId: string, flightId: string, data: Partial<Flight>) =>
    api.patch<Flight>(`/trips/${tripId}/flights/${flightId}`, data).then(r => r.data),
  deleteFlight: (tripId: string, flightId: string) =>
    api.delete(`/trips/${tripId}/flights/${flightId}`),

  listHotels: (tripId: string) => api.get<Hotel[]>(`/trips/${tripId}/hotels`).then(r => r.data),
  createHotel: (tripId: string, data: Partial<Hotel>) =>
    api.post<Hotel>(`/trips/${tripId}/hotels`, data).then(r => r.data),
  updateHotel: (tripId: string, hotelId: string, data: Partial<Hotel>) =>
    api.patch<Hotel>(`/trips/${tripId}/hotels/${hotelId}`, data).then(r => r.data),
  deleteHotel: (tripId: string, hotelId: string) =>
    api.delete(`/trips/${tripId}/hotels/${hotelId}`),

  exportData: (tripId: string) =>
    api.get<TripExportData>(`/trips/${tripId}/export-data`).then(r => r.data),
};
